import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface InvoicesListProps {
  shopId?: string;
}

export const InvoicesList = ({ shopId }: InvoicesListProps) => {
  const { data: invoices, isLoading } = useQuery({
    queryKey: ['billing-invoices', shopId],
    queryFn: async () => {
      if (!shopId) return [];

      const { data, error } = await supabase
        .from('billing_invoices')
        .select('*')
        .eq('shop_id', shopId)
        .order('created_at', { ascending: false })
        .limit(12);

      if (error) throw error;
      return data;
    },
    enabled: !!shopId,
  });

  if (isLoading) {
    return (
      <Card className="p-6">
        <Skeleton className="h-64 w-full" />
      </Card>
    );
  }

  if (!invoices || invoices.length === 0) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Invoices</h2>
        <p className="text-muted-foreground">No invoices yet.</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold mb-4">Recent Invoices</h2>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Period</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((invoice) => (
            <TableRow key={invoice.id}>
              <TableCell>
                {new Date(invoice.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell>
                ${(invoice.amount_paid / 100).toFixed(2)}
              </TableCell>
              <TableCell>
                <Badge variant={invoice.status === 'paid' ? 'default' : 'outline'}>
                  {invoice.status}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {new Date(invoice.period_start).toLocaleDateString()} -{' '}
                {new Date(invoice.period_end).toLocaleDateString()}
              </TableCell>
              <TableCell className="text-right">
                {invoice.invoice_pdf_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(invoice.invoice_pdf_url, '_blank')}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};
